import React from 'react'

export default function DailyAttendanceTable({
  students = [],
  loading = false,
  sortBy = 'rollNo',
  sortOrder = 'asc',
  onSort,
  onStatusChange,
  onReasonChange,
  onMarkAll,
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] text-left text-xs text-on-surface">
          {/* Header */}
          <thead className="border-b border-outline-variant/25 bg-surface-container-low/70 text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant select-none">
            <tr>
              {/* Company / Branch & Course */}
              <th
                onClick={() => onSort('instituteName')}
                className="w-[24%] min-w-[210px] px-5 py-3.5 cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center gap-1">
                  <span>Company(Branch) / Course</span>
                  {sortBy === 'instituteName' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* Student Name / Batches */}
              <th
                onClick={() => onSort('fullName')}
                className="w-[26%] min-w-[220px] px-5 py-3.5 cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center gap-1">
                  <span>Student / Batches</span>
                  {sortBy === 'fullName' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* Roll No (Shifted to Left) */}
              <th
                onClick={() => onSort('rollNo')}
                className="w-20 min-w-[80px] px-3 py-3.5 text-center cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center justify-center gap-1">
                  <span>Roll No</span>
                  {sortBy === 'rollNo' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* Status Column with Quick Mass Select */}
              <th className="w-72 min-w-[280px] px-4 py-3.5">
                <div className="flex items-center justify-between gap-2">
                  <span>Attendance</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      title="Mark All Present"
                      onClick={() => onMarkAll('P')}
                      className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-500/20 transition cursor-pointer"
                    >
                      All P
                    </button>
                    <button
                      type="button"
                      title="Mark All Absent"
                      onClick={() => onMarkAll('A')}
                      className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-[10px] font-bold text-rose-700 hover:bg-rose-500/20 transition cursor-pointer"
                    >
                      All A
                    </button>
                    <button
                      type="button"
                      title="Mark All Holiday"
                      onClick={() => onMarkAll('H')}
                      className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold text-indigo-700 hover:bg-indigo-500/20 transition cursor-pointer"
                    >
                      All H
                    </button>
                  </div>
                </div>
              </th>

              {/* Reason / Remarks */}
              <th className="min-w-[200px] px-5 py-3.5">Reason / Remarks</th>
            </tr>
          </thead>

          {/* Body */}
          <tbody className="divide-y divide-outline-variant/15 font-medium">
            {loading ? (
              <tr>
                <td colSpan={5} className="py-16 text-center text-xs text-on-surface-variant">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="material-symbols-outlined animate-spin text-2xl text-primary">
                      progress_activity
                    </span>
                    <span>Loading student attendance records...</span>
                  </div>
                </td>
              </tr>
            ) : students.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-16 text-center text-xs text-on-surface-variant">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-4xl text-outline-variant">
                      person_search
                    </span>
                    <p className="font-bold text-on-surface">No Students Found</p>
                    <p className="text-[11px]">Try adjusting your search criteria or selected filters.</p>
                  </div>
                </td>
              </tr>
            ) : (
              students.map((student) => {
                const status = student.attendance || 'P'
                const isAbsent = status === 'A'
                return (
                  <tr
                    key={student.id}
                    className="group transition-colors hover:bg-surface-container-low/50"
                  >
                    {/* Company & Branch / Course */}
                    <td className="px-5 py-4 align-top">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-on-surface">
                          {student.instituteName}
                        </span>
                        <span className="text-[11px] text-on-surface-variant font-semibold">
                          ({student.branchName})
                        </span>
                        <span className="mt-1 font-bold text-primary text-[11px]">
                          {student.courseName}
                        </span>
                      </div>
                    </td>

                    {/* Student Name / Batch */}
                    <td className="px-5 py-4 align-top">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-display text-sm font-extrabold text-[#3b82f6]">
                          {student.fullName}
                        </span>
                        <span className="text-[11px] font-medium text-on-surface-variant">
                          {student.batchNames}
                        </span>
                        {student.swipes && (
                          <div className="mt-1 flex items-center gap-1 text-[10px] text-emerald-700 font-semibold">
                            <span className="material-symbols-outlined text-xs">fingerprint</span>
                            <span>Swipes: {student.swipes}</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Roll No */}
                    <td className="px-3 py-4 align-top text-center">
                      <span className="inline-flex items-center justify-center rounded-xl bg-surface-container-high px-2.5 py-1 text-xs font-black text-on-surface">
                        {student.rollNo}
                      </span>
                    </td>

                    {/* Attendance Selector (WhatsApp Policies Aesthetic) */}
                    <td className="px-4 py-4 align-top">
                      <div className="flex items-center gap-2">
                        {/* Present */}
                        <button
                          type="button"
                          onClick={() => onStatusChange(student.id, 'P')}
                          className={`flex-1 rounded-2xl border py-1.5 px-3 text-xs font-bold transition-all cursor-pointer text-center ${status === 'P'
                            ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-800 font-extrabold shadow-2xs'
                            : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                            }`}
                        >
                          Present
                        </button>

                        {/* Absent */}
                        <button
                          type="button"
                          onClick={() => onStatusChange(student.id, 'A')}
                          className={`flex-1 rounded-2xl border py-1.5 px-3 text-xs font-bold transition-all cursor-pointer text-center ${status === 'A'
                            ? 'border-rose-500/50 bg-rose-500/15 text-rose-800 font-extrabold shadow-2xs'
                            : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                            }`}
                        >
                          Absent
                        </button>

                        {/* Holiday */}
                        <button
                          type="button"
                          onClick={() => onStatusChange(student.id, 'H')}
                          className={`flex-1 rounded-2xl border py-1.5 px-3 text-xs font-bold transition-all cursor-pointer text-center ${status === 'H'
                            ? 'border-indigo-500/50 bg-indigo-500/15 text-indigo-800 font-extrabold shadow-2xs'
                            : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                            }`}
                        >
                          Holiday
                        </button>
                      </div>
                    </td>

                    {/* Reason */}
                    <td className="px-5 py-4 align-top">
                      <input
                        type="text"
                        placeholder={isAbsent ? 'Reason for absence...' : 'Optional remarks...'}
                        value={student.reason || ''}
                        onChange={(e) => onReasonChange(student.id, e.target.value)}
                        className={`h-9 w-full rounded-xl border px-3 text-xs outline-none transition ${isAbsent
                          ? 'border-rose-300 bg-rose-50/50 text-rose-900 placeholder:text-rose-400 focus:border-rose-500 focus:ring-1 focus:ring-rose-500/20'
                          : 'border-outline-variant/30 bg-surface-container-low text-on-surface placeholder:text-on-surface-variant/50 focus:border-primary focus:ring-1 focus:ring-primary/20'
                          }`}
                      />
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
